import * as React from 'react';
import {
    Alert, Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress, Grid, Link as MuiLink, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { AI_LABELS, getAiTaskExecution } from '../../Api';
import { AiTaskExecutionDetailDto } from '../../models/dtos/AiDto';
import AiTaskExecutionsGrid from '../../components/ai/AiTaskExecutionsGrid';
import { RunLink } from '../../components/ai/AiRunDetailView';
import {
    aiLoadErrorText, AiUsageStatusChip, CopyButton, formatAiCount, formatAiDate, formatAiMs, formatAiUsd, JsonBlock, StatLine, TextBlock,
} from '../../components/ai/AiShared';

export default function AiTaskExecutionsPage() {
    const [searchParams] = useSearchParams();
    const [reloadKey, setReloadKey] = React.useState(0);
    const initial = {
        taskCode: searchParams.get('taskCode') ?? undefined,
        status: searchParams.get('status') ?? undefined,
        errorCode: searchParams.get('errorCode') ?? undefined,
        endUserId: searchParams.get('endUserId') ?? undefined,
        runId: searchParams.get('runId') ?? undefined,
    };
    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI task executions</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every task execution with its variables and raw output, failures included (kept 30 days; inputs may hold player data).
                        Click a row for the full input and output.
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={() => setReloadKey(k => k + 1)}>Refresh</Button>
            </Stack>
            <AiTaskExecutionsGrid initial={initial} reloadKey={reloadKey} />
        </Stack>
    );
}

function TextWithCopy({ label, text, truncated, maxHeight = 360 }: { label: string; text: string | null; truncated?: boolean; maxHeight?: number }) {
    return (
        <Box>
            <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="subtitle2">{label}</Typography>
                {truncated && <Chip size="small" color="warning" variant="outlined" label="cut at max-chars" />}
                {text && <CopyButton text={text} />}
            </Stack>
            <Box sx={{ fontFamily: 'monospace' }}><TextBlock text={text} maxHeight={maxHeight} /></Box>
        </Box>
    );
}

function ExecutionDetail({ execution: e }: { execution: AiTaskExecutionDetailDto }) {
    const variables = e.variables ?? e.variablesText;
    return (
        <Stack gap={2}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack gap={0.75}>
                                <StatLine label="Status" value={<AiUsageStatusChip status={e.status} />} />
                                {e.errorCode && <StatLine label="Error code" value={e.errorCode} />}
                                <StatLine label="Stop reason" value={e.stopReason ?? '—'} />
                                <StatLine label="Task" value={
                                    <MuiLink component={RouterLink} to={`/ai/tasks/${encodeURIComponent(e.taskCode)}`} underline="hover">{e.taskCode}</MuiLink>
                                } />
                                <StatLine label="Model" value={e.modelKey ?? '—'} />
                                <StatLine label="Caller" value={e.caller ?? '—'} />
                                <StatLine label="End user" value={e.endUserId != null
                                    ? <MuiLink component={RouterLink} to={`/users/${e.endUserId}`} underline="hover">#{e.endUserId}</MuiLink>
                                    : '—'} />
                                <StatLine label="Subject" value={e.subjectType ? `${e.subjectType} / ${e.subjectId ?? '—'}` : '—'} />
                                {e.runId && <StatLine label="Agent run" value={<RunLink runId={e.runId} />} />}
                                {e.workflowCode && (
                                    <StatLine label="Workflow" value={e.workflowRunKey
                                        ? <MuiLink component={RouterLink} to={`/ai/workflows/${encodeURIComponent(e.workflowCode)}/runs/${encodeURIComponent(e.workflowRunKey)}`} underline="hover">{e.workflowCode} · {e.workflowRunKey}</MuiLink>
                                        : e.workflowCode} />
                                )}
                                <StatLine label="Cached" value={e.cached ? 'yes' : 'no'} />
                                <StatLine label="Images" value={e.attachments} />
                                <StatLine label="Tokens in / out" value={`${formatAiCount(e.inputTokens)} / ${formatAiCount(e.outputTokens)}`} />
                                <StatLine label="Cost" value={formatAiUsd(e.costUsd)} />
                                <StatLine label="Latency" value={formatAiMs(e.latencyMs)} />
                                <StatLine label="At" value={formatAiDate(e.createdAt)} />
                            </Stack>
                            {e.error && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="subtitle2" color="error">Error</Typography>
                                    <TextBlock text={e.error} />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Stack gap={1.5}>
                        <Box>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">Input variables</Typography>
                                {e.variablesTruncated && <Chip size="small" color="warning" variant="outlined" label="cut at max-chars" />}
                                {variables != null && <CopyButton text={typeof variables === 'string' ? variables : JSON.stringify(variables, null, 2)} />}
                            </Stack>
                            <JsonBlock value={variables} maxHeight={300} />
                        </Box>
                        {e.outputJson != null && (
                            <Box>
                                <Typography variant="subtitle2">Output JSON</Typography>
                                <JsonBlock value={e.outputJson} maxHeight={300} />
                            </Box>
                        )}
                        <TextWithCopy label={e.status === 'ERROR' ? 'Output text (as the model wrote it)' : 'Output text'} text={e.outputText} truncated={e.outputTruncated} />
                        {e.rejectedOutputText && (
                            <TextWithCopy label="First reply, rejected by the output schema (a repair attempt followed)" text={e.rejectedOutputText} maxHeight={240} />
                        )}
                    </Stack>
                </Grid>
            </Grid>
            <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Model calls ({e.calls.length})</Typography>
                {e.calls.length === 0
                    ? <Typography variant="body2" color="text.secondary">{e.cached ? 'Answered from the result cache.' : 'No model call (stopped before the call, or the call row was deleted).'}</Typography>
                    : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Call</TableCell><TableCell>At</TableCell><TableCell>Status</TableCell><TableCell>Model</TableCell>
                                    <TableCell align="right">Tokens in / out</TableCell><TableCell align="right">Cost</TableCell>
                                    <TableCell align="right">Latency</TableCell><TableCell>Error</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {e.calls.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>#{c.id}</TableCell>
                                        <TableCell>{formatAiDate(c.createdAt)}</TableCell>
                                        <TableCell><AiUsageStatusChip status={c.status} /></TableCell>
                                        <TableCell>{c.modelKey ?? '—'}{c.isFallback ? ' (fallback)' : ''}</TableCell>
                                        <TableCell align="right">{formatAiCount(c.inputTokens)} / {formatAiCount(c.outputTokens)}</TableCell>
                                        <TableCell align="right">{formatAiUsd(c.costUsd)}</TableCell>
                                        <TableCell align="right">{formatAiMs(c.latencyMs)}</TableCell>
                                        <TableCell><Typography variant="caption" color="error">{c.error ?? ''}</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
            </Box>
        </Stack>
    );
}

export function AiTaskExecutionPage() {
    const { executionId } = useParams<{ executionId: string }>();
    const [execution, setExecution] = React.useState<AiTaskExecutionDetailDto | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);

    const load = React.useCallback(async () => {
        if (!executionId) return;
        setLoading(true);
        const detail = await getAiTaskExecution(executionId);
        setLoading(false);
        if (detail == null) {
            setError(aiLoadErrorText(AI_LABELS.taskExecution, `Failed to load task execution ${executionId} (executions are kept 30 days).`));
            return;
        }
        setError(null);
        setExecution(detail);
    }, [executionId]);

    React.useEffect(() => {
        setExecution(null);
        load();
    }, [load]);

    return (
        <Stack gap={2} sx={{ mx: 5, my: 4 }}>
            <Breadcrumbs>
                <MuiLink component={RouterLink} to="/ai" underline="hover">AI</MuiLink>
                <MuiLink component={RouterLink} to="/ai/task-executions" underline="hover">Task executions</MuiLink>
                <Typography color="text.primary" sx={{ fontFamily: 'monospace' }}>{executionId}</Typography>
            </Breadcrumbs>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" gap={1.5} alignItems="center">
                    <Typography variant="h5">Task execution {execution ? `· ${execution.taskCode}` : ''}</Typography>
                    {execution && <AiUsageStatusChip status={execution.status} />}
                </Stack>
                <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {loading && execution == null && <CircularProgress />}
            {execution && <ExecutionDetail execution={execution} />}
        </Stack>
    );
}
